// <AgeVerificationModal />: the blocking ID-verification screen for signed-in accounts.
//   unverified      → form: legal name, DOB (18+), document type, ID photo with preview
//   pending_review  → locked screen until staff approve; rechecks every 20 s
//   rejected        → the reason, and a way to resubmit
//   verified / signed out → nothing
// A native <dialog> (showModal) traps focus and makes the page behind it inert.
// Escape and backdrop clicks can't close it.

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { Session, SupabaseClient } from '@supabase/supabase-js';

export type VerificationStatus = 'loading' | 'signed_out' | 'unverified' | 'pending_review' | 'rejected' | 'verified';

interface Latest {
  status: 'pending_review' | 'verified' | 'rejected';
  rejection_reason: string | null;
  created_at: string;
}

const MAX_BYTES = 10 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const DOCUMENT_TYPES: [string, string][] = [
  ['drivers_license', 'Driver’s license'],
  ['state_id', 'State ID card'],
  ['passport', 'Passport'],
  ['military_id', 'Military ID'],
];
const RECHECK_MS = 20_000;
const formatSize = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function isAdult(dob: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  if (!m) return false;
  const [y, mo, d] = m.slice(1).map(Number);
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < d)) age--;
  return age >= 18;
}

function latestAllowedDob(): string {
  const now = new Date();
  const d = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Tracks the signed-in account's verification status (the latest submission). */
export function useVerification(supabase: SupabaseClient) {
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [latest, setLatest] = useState<Latest | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  const refresh = useCallback(async (s: Session | null) => {
    setSession(s);
    if (!s) {
      setLatest(null);
      return setStatus('signed_out');
    }
    const { data, error } = await supabase.from('age_verifications')
      .select('status, rejection_reason, created_at').eq('user_id', s.user.id)
      .order('created_at', { ascending: false }).limit(1).maybeSingle<Latest>();
    if (error) {
      console.error('verification status', error.message);
      return; // keep the current state; the next check retries
    }
    setLatest(data);
    setStatus(data ? data.status : 'unverified');
  }, [supabase]);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, s) => {
      setTimeout(() => refresh(s), 0); // never call Supabase inside this callback (auth lock)
    });
    return () => data.subscription.unsubscribe();
  }, [supabase, refresh]);

  useEffect(() => {
    if (status !== 'pending_review') return;
    const t = setInterval(async () => refresh((await supabase.auth.getSession()).data.session), RECHECK_MS);
    return () => clearInterval(t);
  }, [status, supabase, refresh]);

  return { status, latest, session, recheck: async () => refresh((await supabase.auth.getSession()).data.session) };
}

/** True while the site-wide 18+ gate (compliance.js) is still showing. */
function useAgeGateOpen() {
  const gated = () => document.documentElement.classList.contains('ah-gated');
  const [open, setOpen] = useState(gated());
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => { if (!gated()) setOpen(false); }, 300);
    return () => clearInterval(t);
  }, [open]);
  return open;
}

export function AgeVerificationModal({ supabase, signOut }: { supabase: SupabaseClient; signOut: () => Promise<void> }) {
  const { status, latest, session, recheck } = useVerification(supabase);
  const ageGateOpen = useAgeGateOpen();
  const [resubmitting, setResubmitting] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const blocking = !ageGateOpen && (status === 'unverified' || status === 'pending_review' || status === 'rejected');

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (blocking && !d.open) d.showModal();
    if (!blocking && d.open) d.close();
    document.documentElement.classList.toggle('ah-verify-locked', blocking);
    if (!blocking) setResubmitting(false);
  }, [blocking]);

  useEffect(() => {
    const d = dialog.current!;
    const keepOpen = (e: Event) => e.preventDefault();
    const reopen = () => { if (d.dataset.blocking === 'true') d.showModal(); };
    const noEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') e.preventDefault(); };
    d.addEventListener('cancel', keepOpen);
    d.addEventListener('close', reopen); // Chromium may close on Escape without user activation
    d.addEventListener('keydown', noEscape);
    return () => {
      d.removeEventListener('cancel', keepOpen);
      d.removeEventListener('close', reopen);
      d.removeEventListener('keydown', noEscape);
    };
  }, []);

  const leave = async () => {
    await signOut();
    location.reload();
  };

  const showForm = status === 'unverified' || (status === 'rejected' && resubmitting);

  return (
    <dialog ref={dialog} class="ahv-dialog" data-blocking={String(blocking)} aria-labelledby="ahv-title" aria-describedby="ahv-desc">
      {blocking && (
        <div class="ahv-body">
          <span class="ahv-eyebrow">ID VERIFICATION REQUIRED</span>
          {showForm && session && (
            <VerificationForm
              session={session}
              resubmission={status === 'rejected'}
              onSubmitted={recheck}
              onSignOut={leave}
            />
          )}
          {status === 'pending_review' && (
            <div class="ahv-stack">
              <div class="ahv-icon" aria-hidden="true">⏳</div>
              <h2 id="ahv-title">Your ID is under review</h2>
              <p id="ahv-desc">
                Your ID is currently under manual review. Access to AfterHourz.tv is restricted until an admin verifies your
                age (typically under 24 hours).
              </p>
              {latest && <p class="ahv-muted">Submitted {new Date(latest.created_at).toLocaleString()}. We’ll email you when it’s done.</p>}
              <div class="ahv-row">
                <button type="button" class="ahv-btn ahv-secondary" onClick={recheck}>Check again</button>
                <button type="button" class="ahv-btn ahv-secondary" onClick={leave}>Sign out</button>
              </div>
            </div>
          )}
          {status === 'rejected' && !resubmitting && (
            <div class="ahv-stack">
              <h2 id="ahv-title">We couldn’t verify your ID</h2>
              <div class="ahv-alert" role="alert" id="ahv-desc">
                <strong>Reason:</strong> {latest?.rejection_reason ?? 'The submission could not be verified.'}
              </div>
              <p class="ahv-muted">Submit a clear, uncropped photo of a valid government-issued photo ID. The name and date of birth must match what you enter.</p>
              <div class="ahv-row">
                <button type="button" class="ahv-btn ahv-primary" onClick={() => setResubmitting(true)}>Submit a clearer photo</button>
                <button type="button" class="ahv-btn ahv-secondary" onClick={leave}>Sign out</button>
              </div>
            </div>
          )}
        </div>
      )}
    </dialog>
  );
}

function VerificationForm({ session, resubmission, onSubmitted, onSignOut }: {
  session: Session;
  resubmission: boolean;
  onSubmitted: () => void;
  onSignOut: () => void;
}) {
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [docType, setDocType] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [consent, setConsent] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) return setPreview('');
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function choose(f: File | undefined) {
    if (!f) return;
    if (!IMAGE_TYPES.includes(f.type)) return setError('Use a JPG, PNG or WebP photo of your ID.');
    if (f.size > MAX_BYTES) return setError('The photo must be 10 MB or smaller.');
    setError('');
    setFile(f);
  }

  async function submit(e: Event) {
    e.preventDefault();
    setError('');
    if (name.trim().length < 2) return setError('Enter your full legal name as it appears on your ID.');
    if (!dob) return setError('Enter your date of birth.');
    if (!isAdult(dob)) return setError('You must be at least 18 years old to use AfterHourz.tv.');
    if (!docType) return setError('Choose the type of ID.');
    if (!file) return setError('Add a photo of your ID.');
    if (!consent) return setError('Please agree to the ID check to continue.');

    const body = new FormData();
    body.set('full_legal_name', name.trim());
    body.set('date_of_birth', dob);
    body.set('document_type', docType);
    body.set('file', file, file.name);
    setBusy(true);
    try {
      const res = await fetch('/api/verify/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body,
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 409) throw new Error(payload.error || `Upload failed (${res.status}).`);
      onSubmitted();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="ahv-stack" onSubmit={submit} noValidate>
      <h2 id="ahv-title">{resubmission ? 'Submit your ID again' : 'Verify your age'}</h2>
      <p id="ahv-desc" class="ahv-muted">
        AfterHourz.tv is for adults only. To keep minors off the platform, every account is checked by a person against a
        government-issued photo ID before it can be used. Your ID is stored privately and only seen by our verification team.
      </p>

      <label class="ahv-field">
        <span>Full legal name</span>
        <input value={name} onInput={(e) => setName(e.currentTarget.value)} autoComplete="name" maxLength={200} required placeholder="As shown on your ID" />
      </label>
      <div class="ahv-grid">
        <label class="ahv-field">
          <span>Date of birth</span>
          <input type="date" value={dob} max={latestAllowedDob()} min="1900-01-01" onInput={(e) => setDob(e.currentTarget.value)} autoComplete="bday" required />
        </label>
        <label class="ahv-field">
          <span>ID type</span>
          <select value={docType} onChange={(e) => setDocType(e.currentTarget.value)} required>
            <option value="" disabled>Choose…</option>
            {DOCUMENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>

      <div class="ahv-field">
        <span>Photo of your ID</span>
        <input ref={input} type="file" accept={IMAGE_TYPES.join(',')} hidden onChange={(e) => choose(e.currentTarget.files?.[0])} />
        <button
          type="button"
          class={`ahv-drop ${dragging ? 'dragging' : ''} ${preview ? 'has-preview' : ''}`}
          onClick={() => input.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); choose(e.dataTransfer?.files[0]); }}
        >
          {preview ? (
            <>
              <img src={preview} alt="Preview of your ID photo" />
              <span class="ahv-muted">{file?.name} · {formatSize(file?.size ?? 0)} · click to replace</span>
            </>
          ) : (
            <>
              <strong>Drop a photo here or click to choose</strong>
              <span class="ahv-muted">JPG, PNG or WebP · up to 10 MB · all four corners visible, no glare</span>
            </>
          )}
        </button>
      </div>

      <label class="ahv-check">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.currentTarget.checked)} />
        <span>I agree to AfterHourz.tv storing this ID and reviewing it to verify my age, as described in the{' '}
          <a href="/legal/terms.html" target="_blank" rel="noopener">Terms</a>.</span>
      </label>

      {error && <div class="ahv-alert" role="alert">{error}</div>}
      <div class="ahv-row">
        <button type="submit" class="ahv-btn ahv-primary" disabled={busy}>{busy ? 'Uploading securely…' : 'Submit for review'}</button>
        <button type="button" class="ahv-btn ahv-secondary" onClick={onSignOut}>Sign out</button>
      </div>
    </form>
  );
}
